import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "../services/apiService";
import type {
  GenerationResponse,
  Pattern,
  PatternGenerationParams,
  TilesetGenerationParams,
} from "../types/generation";

export interface GenerationStatus {
  id: number;
  status: string;
  progress?: GenerationProgress;
  result?: string;
  error?: string;
}

async function getGenerationStatus(id: number): Promise<GenerationStatus> {
  const { data } = await api.get(`/patterning/status/${id}`);
  return data;
}

async function getExtractedPatterns(id: string): Promise<Pattern[]> {
  const { data } = await api.get(`/generation/${id}/patterns`);
  return data;
}

export function useGenerationStatus(id?: number) {
  return useQuery<GenerationStatus>({
    queryKey: ["generation", id],
    queryFn: async () => {
      if (!id) throw new Error("No generation ID provided");
      return getGenerationStatus(id);
    },
    enabled: !!id,
    refetchInterval: (data) =>
      data?.status === "completed" || data?.status === "failed" ? false : 1000,
  });
}

export function useExtractedPatterns(id: string | null) {
  return useQuery<Pattern[], Error, Pattern[], QueryKey>({
    queryKey: ["patterns", id],
    queryFn: async ({ queryKey: [_, genId] }) => {
      if (!genId) throw new Error("Generation ID is required");
      return getExtractedPatterns(genId);
    },
    enabled: !!id,
  });
}

export function useTilesetGeneration() {
  const queryClient = useQueryClient();

  return useMutation<
    GenerationResponse,
    Error,
    {
      file: File;
      params: TilesetGenerationParams;
    }
  >({
    mutationFn: async ({ file, params }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("params", JSON.stringify(params));

      const { data } = await api.post("/generation/tiling", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return {
        ...data,
        status: data.status || "pending",
      };
    },
    onSuccess: (data) => {
      queryClient.prefetchQuery({
        queryKey: ["generation", data.id] as QueryKey,
        queryFn: () => getGenerationStatus(data.id),
      });
    },
  });
}

export function usePatternGeneration() {
  const queryClient = useQueryClient();

  const generatePatterns = async (
    file: File,
    params: PatternGenerationParams,
  ): Promise<GenerationResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    // Create request data
    const requestData = {
      pattern: file.name.replace(".png", ""),
      width: params.width,
      height: params.height,
      pattern_size: params.patternSize,
    };

    // Convert request data to string and append as form field
    formData.append(
      "request",
      new Blob([JSON.stringify(requestData)], {
        type: "application/json",
      }),
    );

    const { data } = await api.post("/patterning/generate", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return {
      ...data,
      status: data.status || "pending",
    };
  };

  return useMutation<
    GenerationResponse,
    Error,
    {
      file: File;
      params: PatternGenerationParams;
    }
  >({
    mutationFn: ({ file, params }) => generatePatterns(file, params),
    onSuccess: (data) => {
      queryClient.prefetchQuery({
        queryKey: ["generation", data.id] as QueryKey,
      });
    },
  });
}

export function useWFCGeneration() {
  const queryClient = useQueryClient();

  return useMutation<
    GenerationResponse,
    Error,
    {
      type: string;
      settings: {
        file: File;
        params: TilesetGenerationParams | PatternGenerationParams;
      };
    }
  >({
    mutationFn: async ({ type, settings }) => {
      const formData = new FormData();
      formData.append("file", settings.file);
      formData.append("params", JSON.stringify(settings.params));

      const response = await api.post(`/generation/${type}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.prefetchQuery({
        queryKey: ["generation", data.id] as QueryKey,
        queryFn: () => api.get(`/generation/${data.id}/status`),
      });
    },
  });
}
